import React from 'react';
import { Loader2 } from 'lucide-react';

type Props = {
  label: string;
  value: number;
  target: number;
  formatValue?: (n: number) => string;
  formatTarget?: (n: number) => string;
  /** Show spinner instead of figures while KPI API / applications load */
  loading?: boolean;
  /** Extra regel onder de donut (bijv. vacature-statusverdeling) */
  footLine?: string;
};

/** Progress ring: green when on/above target, warm tones when below */
export const DonutKpiCard: React.FC<Props> = ({
  label,
  value,
  target,
  formatValue = (n) => String(Math.round(n)),
  formatTarget = (n) => String(Math.round(n)),
  loading = false,
  footLine,
}) => {
  if (loading) {
    return (
      <div className="group relative flex min-h-[200px] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white/90 p-4 shadow-sm shadow-slate-200/40 backdrop-blur-sm dark:border-slate-700 dark:bg-slate-900/80">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</span>
        <div className="flex flex-1 flex-col items-center justify-center gap-2 py-6">
          <Loader2 className="h-8 w-8 animate-spin text-[#e51b23]" aria-hidden />
          <p className="text-center text-[11px] font-semibold text-slate-500">Cijfers laden…</p>
        </div>
      </div>
    );
  }

  const pct = target > 0 ? (value / target) * 100 : 0;
  const displayPct = Math.round(pct);
  const onTrack = pct >= 100;
  const mid = pct >= 70 && pct < 100;
  const strokeColor = onTrack ? '#22c55e' : mid ? '#f59e0b' : '#fb923c';
  const trackColor = '#f1f5f9';
  const r = 36;
  const c = 2 * Math.PI * r;
  const arcPct = Math.min(pct, 100);
  const dash = (arcPct / 100) * c;

  const behind = target > 0 && value < target ? target - value : 0;
  const behindUnit = behind > 0 ? Math.round(behind) : 0;

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/90 p-4 shadow-sm shadow-slate-200/40 backdrop-blur-sm transition-all hover:border-red-200/80 hover:shadow-md dark:border-slate-700 dark:bg-slate-900/80">
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 leading-tight">
          {label}
        </span>
        <span className="text-xl font-black tabular-nums text-slate-900 dark:text-white shrink-0">{formatValue(value)}</span>
      </div>
      <p className="mt-1 text-[10px] font-medium text-slate-400 dark:text-slate-500">
        Doel: <span className="text-slate-600 dark:text-slate-300">{formatTarget(target)}</span>
      </p>

      <div className="relative mx-auto mt-3 flex h-[104px] w-[104px] items-center justify-center">
        <svg className="h-full w-full -rotate-90 transform" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r={r} fill="none" stroke={trackColor} strokeWidth="10" className="dark:stroke-slate-700" />
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke={strokeColor}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c}`}
            className="transition-all duration-700 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className={`text-lg font-black tabular-nums ${onTrack ? 'text-emerald-600' : mid ? 'text-amber-600' : 'text-orange-500'}`}>
            {displayPct}%
          </span>
          <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">van doel</span>
        </div>
      </div>

      {behind > 0.05 && (
        <p className="mt-2 text-center text-[10px] font-bold text-orange-600 dark:text-orange-400">
          {behindUnit} achter op doel
        </p>
      )}
      {footLine && (
        <p className="mt-2 text-center text-[9px] font-semibold leading-snug text-slate-500 dark:text-slate-400 line-clamp-3">
          {footLine}
        </p>
      )}
    </div>
  );
};
